'use client';

import type { ReactNode } from 'react';

import { BaseDrawer } from './base-drawer';
import { DrawerModeProvider, useInDrawer } from './drawer-mode-context';
import { Modal } from './modal';
import { useDrawerMaxHeight } from './use-drawer-max-height';
import { useViewportSize } from '../lib/viewport-size/index';

interface AdaptiveDialogProps {
	open: boolean;
	onClose: () => void;
	/**
	 * Accessible name for the dialog. Rendered sr-only as BaseDrawer's title on
	 * mobile and passed as the Modal's ariaLabel on desktop. The visible heading
	 * stays part of `children`.
	 */
	title: string;
	children: ReactNode;
	/**
	 * Actions that must stay in view while the body scrolls. On mobile the
	 * footer is a fixed sibling under the sheet's scroll region (above the
	 * safe-area padding); on desktop it follows the body inside the Modal.
	 * The slot brings its own horizontal / bottom padding; the body keeps its
	 * own. Absent by default: nothing changes for callers without it.
	 */
	footer?: ReactNode;
	/** Forwarded to the desktop Modal's className (width clamp, etc.). No effect on the mobile drawer. */
	desktopClassName?: string;
	/** Mobile drawer safe-area bottom padding preset (default: comfortable). */
	drawerPadding?: 'comfortable' | 'compact';
	/**
	 * Mobile drawer max height. Defaults to the keyboard-aware value so forms
	 * stay above the on-screen keyboard; the body scrolls beyond it.
	 */
	drawerMaxHeight?: string;
	/** Vaul dismissible flag (mobile swipe-to-close). */
	dismissible?: boolean;
	/** Trap keyboard focus inside the desktop Modal. */
	trapFocus?: boolean;
	/** Show the desktop Modal's close (X) button (default true). Ignored on mobile - the drawer uses its handle/swipe. */
	showCloseButton?: boolean;
}

/**
 * Standard adaptive dialog shell per the ShareSpaceDialog convention: a bottom
 * drawer on mobile, a centered modal on desktop. Factored out so the nine-odd
 * dialogs that need this branch don't each re-implement the
 * `useViewportSize().isMobile` split.
 *
 * The mobile body is wrapped in a keyboard-aware scroll region and a
 * DrawerModeProvider (mirroring PopoverDrawer) so a Dropdown/Popover opened from
 * inside the dialog stacks as a nested vaul drawer rather than a detached second
 * root, and so tall/forms scroll within the sheet.
 */
export const AdaptiveDialog = ({
	open,
	onClose,
	title,
	children,
	footer,
	desktopClassName,
	drawerPadding,
	drawerMaxHeight,
	dismissible,
	trapFocus,
	showCloseButton = true,
}: AdaptiveDialogProps) => {
	const keyboardAwareMaxHeight = useDrawerMaxHeight();
	const { isMobile } = useViewportSize();
	// A dialog opened from inside another drawer stacks as a nested vaul drawer -
	// same pattern Popover uses for PopoverDrawer. Without this the second drawer
	// would be a detached root that the parent's pointer blocker shuts out.
	const nested = useInDrawer();

	if (isMobile) {
		return (
			<BaseDrawer
				open={open}
				onClose={onClose}
				title={title}
				padding={drawerPadding}
				maxHeight={drawerMaxHeight ?? keyboardAwareMaxHeight}
				dismissible={dismissible}
				nested={nested}
			>
				<div className="min-h-0 flex-1 overflow-y-auto">
					<DrawerModeProvider>{children}</DrawerModeProvider>
				</div>
				{footer && <div className="shrink-0">{footer}</div>}
			</BaseDrawer>
		);
	}

	return (
		<Modal
			isOpen={open}
			onClose={onClose}
			ariaLabel={title}
			className={desktopClassName}
			trapFocus={trapFocus}
			showCloseButton={showCloseButton}
		>
			{children}
			{footer}
		</Modal>
	);
};
